import { RuntimeContent } from "./runtime"
import { ContentSnapshot, ContentStore, PreparedContent } from "./store"

export interface PublicationIdentity { id: string; revision: string }
export type PublicationCommand = PublicationIdentity & { kind: "prepare" | "activate" | "abort" }
/** A failed reply says why, so an aborted publication can tell its operator. */
export type PublicationReply = PublicationIdentity & { kind: "ready" | "activated" | "failed"; reason?: string }

interface Publication {
    identity: PublicationIdentity
    handle: PreparedContent
    peers: Map<string, "preparing" | "ready" | "activating" | "activated">
    deadline: number
    committed: boolean
}

/** Transport-independent barrier. The host calls commit at a tick boundary and gates input/world
 * delivery with canUseActiveContent until each peer acknowledges activation. */
export class ContentPublisher {
    private pending?: Publication
    private sequence = 0
    private blocked = new Set<string>()
    constructor(
        private runtime: RuntimeContent,
        private send: (peer: string, command: PublicationCommand) => void,
        private quarantine: (peer: string, reason: string) => void,
        private now: () => number = Date.now,
        private timeoutMs = 30000,
        private prepareTimeoutMs = timeoutMs,
    ) {}

    /** Why the last preparation was abandoned, for whoever started it. */
    abortReason?: string
    get identity(): PublicationIdentity | undefined { return this.pending && { ...this.pending.identity } }
    get committed(): boolean { return this.pending?.committed === true }

    prepare(manifest: unknown, catalog: unknown, peers: readonly string[]): PublicationIdentity {
        if (this.pending?.committed) throw new Error("Content activation acknowledgements are still pending")
        if (new Set(peers).size !== peers.length) throw new Error("Duplicate publication participants")
        if (peers.some(peer => this.blocked.has(peer))) throw new Error("Quarantined connection cannot participate")
        // Invalid candidates leave an earlier preparation usable.
        const handle = this.runtime.prepare(manifest, catalog)
        this.abort("a newer publication replaced it")
        this.abortReason = undefined
        const identity = Object.freeze({ id: `content-${++this.sequence}`, revision: handle.revision })
        const pending: Publication = { identity, handle, peers: new Map(peers.map(peer => [peer, "preparing"])), deadline: this.now() + this.prepareTimeoutMs, committed: false }
        this.pending = pending
        for (const peer of peers) {
            if (this.pending !== pending) break
            try { this.send(peer, { ...identity, kind: "prepare" }) }
            catch { this.abort(`could not reach player connection ${peer}`); throw new Error(`Cannot prepare content on peer ${peer}`) }
        }
        return identity
    }

    receive(peer: string, reply: PublicationReply): void {
        const p = this.pending
        if (!p || reply.id !== p.identity.id || reply.revision !== p.identity.revision || !p.peers.has(peer)) return
        if (this.expire()) return
        if (reply.kind === "failed") {
            if (!p.committed) this.abort(`a player's game could not prepare it: ${typeof reply.reason === "string" && reply.reason ? reply.reason.slice(0, 300) : "no reason given (an older game client sends none; reload it)"}`)
            else { p.peers.delete(peer); this.blockPeer(peer, "contentActivationFailed"); this.finish() }
        } else if (reply.kind === "ready" && p.peers.get(peer) === "preparing") {
            p.peers.set(peer, "ready")
        } else if (reply.kind === "activated" && p.peers.get(peer) === "activating") {
            p.peers.set(peer, "activated")
            this.finish()
        }
    }

    /** The caller must supply the current authenticated connection IDs, including suspended peers.
     * Membership changes abort preparation; joins should wait until the barrier finishes. */
    commit(peers: readonly string[]): boolean {
        const p = this.pending
        if (!p || p.committed || this.expire()) return false
        if (peers.length !== p.peers.size || new Set(peers).size !== peers.length || peers.some(peer => !p.peers.has(peer))) {
            this.abort("a player joined or left while it was being prepared; try again")
            return false
        }
        if ([...p.peers.values()].some(state => state !== "ready")) return false
        try { this.runtime.activate(p.handle) } catch (error) { this.abort(); throw error }
        p.committed = true
        p.deadline = this.now() + this.timeoutMs
        // Set all states before sending; a test/local transport may reply synchronously.
        for (const peer of p.peers.keys()) p.peers.set(peer, "activating")
        for (const peer of [...p.peers.keys()]) {
            try { this.send(peer, { ...p.identity, kind: "activate" }) }
            catch { p.peers.delete(peer); this.blockPeer(peer, "contentActivationFailed") }
        }
        this.finish()
        return true
    }

    canUseActiveContent(peer: string): boolean {
        if (this.blocked.has(peer)) return false
        const p = this.pending
        return !p?.committed || p.peers.get(peer) === "activated"
    }

    /** Poll even when the simulation is paused. After commit there is no distributed rollback:
     * peers that cannot acknowledge must leave before gameplay resumes for them. */
    expire(): boolean {
        const p = this.pending
        if (!p || this.now() < p.deadline) return false
        if (!p.committed) this.abort("a player's game did not finish preparing it in time")
        else {
            for (const [peer, state] of p.peers) if (state !== "activated") this.blockPeer(peer, "contentActivationTimeout")
            this.pending = undefined
        }
        return true
    }

    abort(reason = "it was cancelled"): void {
        const p = this.pending
        if (!p) return
        if (p.committed) throw new Error("Committed publication cannot be aborted")
        this.pending = undefined
        this.abortReason = reason
        this.runtime.discard(p.handle)
        for (const peer of p.peers.keys()) {
            try { this.send(peer, { ...p.identity, kind: "abort" }) } catch { /* Old content remains active. */ }
        }
    }

    private finish() {
        if (this.pending?.committed && [...this.pending.peers.values()].every(state => state === "activated")) this.pending = undefined
    }

    private blockPeer(peer: string, reason: string) {
        this.blocked.add(peer)
        try { this.quarantine(peer, reason) } catch { /* The input/delivery gate remains closed. */ }
    }
}

/** Renderer generations swap references synchronously and return an undo operation. discard must
 * only free an unpublished generation; retired active resources need renderer-owned lifetimes. */
export interface StagedContentResources { activate(): () => void; discard(): void }
/** Media a server sends with a prepare because no web host serves it (a release held only in the
 * server's memory): base64 bytes by media path, checked against the manifest like any download. */
export type InlineContentFiles = Record<string, string>
export type ContentStage = (snapshot: ContentSnapshot, signal: AbortSignal, inline?: InlineContentFiles) => Promise<StagedContentResources>

/** One per socket/session. Dispose on disconnect so old asynchronous work cannot reply on a new
 * connection. Invoke activate at the client frame boundary before processing new world messages. */
export class ContentParticipant {
    private pending?: { identity: PublicationIdentity; controller: AbortController; handle?: PreparedContent; resources?: StagedContentResources }
    constructor(private runtime: RuntimeContent, private stage: ContentStage, private reply: (reply: PublicationReply) => void) {}

    async prepare(identity: PublicationIdentity, manifest: unknown, catalog: unknown, inline?: InlineContentFiles): Promise<void> {
        this.dispose()
        const p = { identity: { id: identity.id, revision: identity.revision }, controller: new AbortController() } as NonNullable<ContentParticipant["pending"]>
        this.pending = p
        try {
            // Validate and detach before awaiting downloads. Runtime selectors are prepared last,
            // after any modules imported during staging have registered their bindings.
            const candidate = new ContentStore(manifest, catalog).active
            if (candidate.catalog.revision !== identity.revision || !identity.id) throw new Error("Publication identity mismatch")
            const resources = await this.stage(candidate, p.controller.signal, inline)
            if (this.pending !== p) { resources.discard(); return }
            p.resources = resources
            p.handle = this.runtime.prepare(candidate.manifest, candidate.catalog)
            this.reply({ ...p.identity, kind: "ready" })
        } catch (error) {
            if (this.pending !== p) return
            this.dispose()
            this.reply({ ...p.identity, kind: "failed", reason: String((error as Error)?.message || error).slice(0, 300) })
        }
    }

    activate(identity: PublicationIdentity): boolean {
        const p = this.pending
        if (!p || p.identity.id !== identity.id || p.identity.revision !== identity.revision) return false
        let undo: (() => void) | undefined
        try {
            if (!p.handle || !p.resources) throw new Error("Content is not ready")
            undo = p.resources.activate()
            this.runtime.activate(p.handle)
        } catch {
            try { undo?.() } finally { this.dispose() }
            this.reply({ ...p.identity, kind: "failed" })
            return false
        }
        this.pending = undefined
        this.reply({ ...p.identity, kind: "activated" })
        return true
    }

    abort(identity: PublicationIdentity) {
        if (this.pending?.identity.id === identity.id && this.pending.identity.revision === identity.revision) this.dispose()
    }

    dispose(): void {
        const p = this.pending
        this.pending = undefined
        if (!p) return
        p.controller.abort()
        if (p.handle) this.runtime.discard(p.handle)
        p.resources?.discard()
    }
}
